import { useId, type JSX } from 'react';

import { Checkbox } from '../../primitives/checkbox';
import { Label } from '../../primitives/label';
import { LoadingAddon } from '../../primitives/loading-addon';
import { Skeleton } from '../../primitives/skeleton';
import { cn } from '../../utils/cn';
import { OptionInfoField, OptionInfoPopover } from '../option-info-popover';

export type CheckboxModelModel = {
  infoFields?: OptionInfoField[];
  value: string;
  item: string | JSX.Element;
}[];

interface CheckboxModelProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Checkbox>,
  'type' | 'value' | 'onValueChange'
> {
  isLoading?: boolean;
  model: CheckboxModelModel;
  type?: 'group' | 'single';
  value?: string[];
  onValueChange?: (value: string[]) => void;
}

export function CheckboxModel({
  className,
  disabled = false,
  isLoading = false,
  model,
  type = 'group',
  value = [],
  onValueChange,
  ...props
}: CheckboxModelProps) {
  const baseId = useId(); // avoids collision between components with the same label value

  const hasValues = model.some((item) => {
    const value = item?.value;
    return typeof value === 'string' ? value.trim() !== '' : value != null;
  });

  return (
    <div
      className={cn(
        'relative grid',
        {
          'gap-0 rounded-lg border border-input shadow-xs': type === 'group',
          'gap-2': type === 'single',
          'cursor-not-allowed border-input/50': disabled || isLoading,
        },
        className,
      )}
    >
      {hasValues ? (
        model.map((modelItem) => {
          if (!modelItem?.value) return null;

          const id = `${baseId}-checkbox-${modelItem.value}`;
          const isChecked = value.includes(modelItem.value);

          return (
            <Label id={props?.id ? `${props.id}-${id}` : undefined} htmlFor={id} key={id}>
              <div
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-lg py-2 text-base hover:bg-accent',
                  {
                    'border border-input px-4': type === 'single',
                    'm-[2px] px-4': type === 'group',
                    'text-muted-foreground hover:cursor-not-allowed hover:bg-transparent':
                      disabled || isLoading,
                  },
                )}
              >
                <Checkbox
                  checked={isChecked}
                  disabled={disabled || isLoading}
                  id={id}
                  onCheckedChange={(checked) => {
                    const next = checked
                      ? [...value, modelItem.value]
                      : value.filter((item) => item !== modelItem.value);
                    onValueChange?.(next);
                  }}
                />
                <div className="inline-flex items-center gap-1">
                  {modelItem.item}
                  <OptionInfoPopover fields={modelItem.infoFields} />
                </div>
              </div>
            </Label>
          );
        })
      ) : (
        <div className="flex h-14 items-center px-4">
          {model.length !== 0 && !hasValues ? (
            <p className="text-base text-muted-foreground">
              La fuente de datos no contiene valores válidos
            </p>
          ) : (
            <Skeleton className="h-8 w-32" />
          )}
        </div>
      )}
      {isLoading && <LoadingAddon type="plain" />}
    </div>
  );
}
